import React, { useState, useEffect, useRef, useCallback } from "react";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import { useNavigate, Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
  FiArrowLeft,
  FiChevronRight,
  FiUsers,
  FiUserPlus,
  FiZap,
  FiShare2,
  FiDollarSign,
  FiSearch,
  FiRefreshCw,
  FiExternalLink,
  FiMail,
  FiSlash,
  FiCopy,
  FiCheck,
  FiX,
  FiCalendar,
  FiSend,
  FiAlertTriangle,
  FiCheckSquare,
  FiSquare,
  FiArrowRight,
  FiTrash2,
  FiAlertCircle,
} from "react-icons/fi";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const avatarColors = [
  "bg-[#ffffcc] text-[#999900]",
  "bg-purple-100 text-purple-600",
  "bg-green-100 text-green-600",
  "bg-orange-100 text-orange-600",
  "bg-rose-100 text-rose-600",
  "bg-teal-100 text-teal-600",
  "bg-blue-100 text-blue-600",
];

const getAvatarColor = (name = "") =>
  avatarColors[(name || "U").charCodeAt(0) % avatarColors.length];

const Spinner = () => (
  <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
);

// Strict date comparison: matches today in local timezone OR UTC OR YYYY-MM-DD prefix
const isToday = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return false;

  const today = new Date();

  // Local calendar match
  const matchLocal =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();

  // UTC calendar match
  const matchUtc =
    d.getUTCFullYear() === today.getUTCFullYear() &&
    d.getUTCMonth() === today.getUTCMonth() &&
    d.getUTCDate() === today.getUTCDate();

  // Prefix match (YYYY-MM-DD)
  const todayYMD = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const todayUtcYMD = today.toISOString().split("T")[0];
  const matchStr =
    typeof dateStr === "string" &&
    (dateStr.startsWith(todayYMD) || dateStr.startsWith(todayUtcYMD));

  return matchLocal || matchUtc || matchStr;
};

const formatTimeOnly = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const TodayUsers = () => {
  const navigate = useNavigate();
  const [allTodayUsers, setAllTodayUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [providerFilter, setProviderFilter] = useState("All");
  const [miningFilter, setMiningFilter] = useState("All");
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedId, setCopiedId] = useState(null);
  const itemsPerPage = 15;
  const debounceTimer = useRef(null);

  // Email Modal State
  const [mailModalOpen, setMailModalOpen] = useState(false);
  const [selectedUserForMail, setSelectedUserForMail] = useState(null);
  const [mailSubject, setMailSubject] = useState("");
  const [mailContent, setMailContent] = useState("");
  const [mailLoading, setMailLoading] = useState(false);

  // Ban Modal State
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [selectedUserForBan, setSelectedUserForBan] = useState(null);
  const [banReason, setBanReason] = useState("Violation of terms / Multi-accounting");
  const [revertCoins, setRevertCoins] = useState(true);
  const [sendWarningEmail, setSendWarningEmail] = useState(true);
  const [banLoading, setBanLoading] = useState(false);

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUserForDelete, setSelectedUserForDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setCurrentPage(1);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebouncedSearch(value), 300);
  };

  const copyText = (text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Fetch only users registered TODAY
  const fetchTodayUsers = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      let todayList = [];
      let loadedFromDedicatedBackend = false;

      // 1. Try dedicated backend endpoint: /users/today
      try {
        const res = await fetch(`${baseUrl}/users/today?perPage=500`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.status === true && Array.isArray(data.data)) {
            // Verify genuinely today's users only (do not accept entire DB)
            const genuineToday = data.data.filter((u) => isToday(u.createdAt));
            if (genuineToday.length > 0 && genuineToday.length === data.data.length) {
              todayList = genuineToday;
              loadedFromDedicatedBackend = true;
            }
          }
        }
      } catch (e) {
        // Dedicated endpoint not yet deployed on remote server, use smart scanner below
      }

      // 2. If not from dedicated endpoint, scan newest users from /users/list until we hit yesterday
      if (!loadedFromDedicatedBackend) {
        let page = 1;
        let hasMoreToday = true;
        const scanBatchSize = 100;

        while (hasMoreToday && page <= 5) {
          const res = await fetch(
            `${baseUrl}/users/list?status=All&page=${page}&perPage=${scanBatchSize}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          const data = await res.json();
          if (data.status === true && Array.isArray(data.data) && data.data.length > 0) {
            for (const user of data.data) {
              if (isToday(user.createdAt)) {
                todayList.push(user);
              } else {
                // Encountered record before today! Stop scanning immediately!
                hasMoreToday = false;
                break;
              }
            }

            if (data.data.length < scanBatchSize || !hasMoreToday) {
              hasMoreToday = false;
            } else {
              page++;
            }
          } else {
            hasMoreToday = false;
          }
        }
      }

      setAllTodayUsers(todayList);
      if (isRefresh) {
        toast.success(`Loaded ${todayList.length} user(s) registered today!`);
      }
    } catch (err) {
      setError(err.message || "Failed to load today's users");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchTodayUsers(false);
  }, [fetchTodayUsers]);

  // Client-side Filtering & Sorting on the full list of today's users
  const filteredUsers = allTodayUsers
    .filter((u) => {
      // Search filter
      if (debouncedSearch.trim()) {
        const term = debouncedSearch.toLowerCase();
        const matches =
          (u.username && u.username.toLowerCase().includes(term)) ||
          (u.email && u.email.toLowerCase().includes(term)) ||
          (u.userId && String(u.userId).includes(term)) ||
          (u.id && String(u.id).includes(term)) ||
          (u.referralCode && u.referralCode.toLowerCase().includes(term)) ||
          (u.referralBy && u.referralBy.toLowerCase().includes(term)) ||
          (u.uuid && u.uuid.toLowerCase().includes(term));
        if (!matches) return false;
      }

      // Provider filter
      if (providerFilter !== "All") {
        const p = (u.providerType || "email").toLowerCase();
        if (p !== providerFilter.toLowerCase()) return false;
      }

      // Mining filter
      if (miningFilter === "active" && !u.isMiningActive) return false;
      if (miningFilter === "inactive" && u.isMiningActive) return false;

      return true;
    })
    .sort((a, b) => {
      if (sortBy === "highest_coins") {
        return (parseFloat(b.coin) || 0) - (parseFloat(a.coin) || 0);
      }
      if (sortBy === "oldest") {
        return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      }
      // default: newest
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });

  // Calculate Metrics from ALL today's users
  const totalTodayCount = allTodayUsers.length;
  const activeMinersToday = allTodayUsers.filter((u) => u.isMiningActive).length;
  const referredUsersToday = allTodayUsers.filter(
    (u) => u.referralBy && u.referralBy.trim() !== ""
  ).length;
  const totalCoinsToday = allTodayUsers.reduce(
    (sum, u) => sum + (parseFloat(u.coin) || 0),
    0
  );

  // Pagination for displayed slice
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage) || 1;
  const displayedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Open Direct Email Modal
  const handleOpenMailModal = (user) => {
    setSelectedUserForMail(user);
    setMailSubject(`Welcome to Zeta Network - Account #${user.userId || user.id}`);
    setMailContent(`Hello ${user.username || "User"},

Welcome to Zeta Network! We noticed you registered your account today.

If you have any questions about mining, rewards, or community events, please feel free to reach out.

Best regards,
Zeta Admin Team`);
    setMailModalOpen(true);
  };

  // Send Direct Email
  const handleSendEmail = async () => {
    if (!selectedUserForMail || !selectedUserForMail.email) {
      toast.error("User email not available");
      return;
    }
    setMailLoading(true);
    const token = localStorage.getItem("token");
    try {
      const payload = {
        email: selectedUserForMail.email,
        subject: mailSubject,
        content: mailContent,
      };

      let sent = false;
      try {
        const r1 = await fetch(`${baseUrl}/users/email/warning`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
        if (r1.ok) sent = true;
      } catch (e) {}

      if (!sent) {
        try {
          const r2 = await fetch(`${baseUrl}/auth/email/warning`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });
          if (r2.ok) sent = true;
        } catch (e) {}
      }

      if (!sent) {
        await fetch(`${baseUrl}/auth/email/pioneer`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
      }

      toast.success(`Email sent to ${selectedUserForMail.email}`);
      setMailModalOpen(false);
    } catch (err) {
      toast.error(err.message || "Failed to dispatch email");
    } finally {
      setMailLoading(false);
    }
  };

  // Open Ban Modal
  const handleOpenBanModal = (user) => {
    setSelectedUserForBan(user);
    setBanReason("Violation of terms / Duplicate registration");
    setRevertCoins(true);
    setSendWarningEmail(true);
    setBanModalOpen(true);
  };

  // Confirm Ban
  const handleConfirmBan = async () => {
    if (!selectedUserForBan) return;
    setBanLoading(true);
    const token = localStorage.getItem("token");
    const targetId = selectedUserForBan.userId || selectedUserForBan.id;
    try {
      await fetch(`${baseUrl}/users/ban/${targetId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          isBanned: true,
          status: "Banned",
          reason: banReason,
          revertCoins,
          coin: revertCoins ? 0 : selectedUserForBan.coin,
        }),
      });

      if (sendWarningEmail && selectedUserForBan.email) {
        try {
          await fetch(`${baseUrl}/users/email/warning`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              email: selectedUserForBan.email,
              subject: "Account Suspension Notice - Zeta Network",
              content: `Dear ${selectedUserForBan.username || "User"},\n\nYour account has been banned due to: ${banReason}.\n\nBest regards,\nZeta Security Team`,
            }),
          });
        } catch (e) {}
      }

      setAllTodayUsers((prev) =>
        prev.map((u) =>
          (u.userId || u.id) === targetId
            ? { ...u, isBanned: true, status: "Banned", coin: revertCoins ? 0 : u.coin }
            : u
        )
      );

      toast.success(`User #${targetId} banned successfully`);
      setBanModalOpen(false);
    } catch (err) {
      toast.error(err.message || "Failed to ban user");
    } finally {
      setBanLoading(false);
    }
  };

  // Open Delete Modal
  const handleOpenDeleteModal = (user) => {
    setSelectedUserForDelete(user);
    setDeleteModalOpen(true);
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!selectedUserForDelete) return;
    setDeleteLoading(true);
    const targetId = selectedUserForDelete.userId || selectedUserForDelete.id;
    const token = localStorage.getItem("token");

    try {
      let res = await fetch(`${baseUrl}/users/delete/${targetId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      let result = await res.json().catch(() => ({}));

      if (!res.ok || result?.status === false) {
        res = await fetch(`${baseUrl}/users/${targetId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        result = await res.json().catch(() => ({}));
      }

      if (res.ok && result?.status !== false) {
        toast.success(result?.message || `User #${targetId} deleted successfully`);
        setAllTodayUsers((prev) =>
          prev.filter((u) => (u.userId || u.id) !== targetId)
        );
        setDeleteModalOpen(false);
        setSelectedUserForDelete(null);
      } else {
        toast.error(result?.message || "Failed to delete user");
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete user");
    } finally {
      setDeleteLoading(false);
    }
  };

  const todayFormatted = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <div className="p-5 sm:p-8 space-y-6 w-full">
          {/* Breadcrumbs & Header Title */}
          <div className="space-y-3">
            <div className="flex items-center flex-wrap gap-1.5 text-xs text-gray-500 font-medium">
              <Link
                to="/user"
                className="hover:text-black hover:underline transition flex items-center gap-1"
              >
                Users
              </Link>
              <FiChevronRight size={12} className="text-gray-400 flex-shrink-0" />
              <span className="text-gray-900 font-bold bg-[#ffffcc] px-2 py-0.5 rounded">
                Today's Registered Users
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (window.history.length > 1) {
                      navigate(-1);
                    } else {
                      navigate("/dashboard");
                    }
                  }}
                  className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-700 hover:text-black transition shadow-xs cursor-pointer flex items-center justify-center"
                  title="Back to Previous Page"
                  aria-label="Back"
                >
                  <FiArrowLeft size={18} />
                </button>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                      Today's Registered Users
                    </h1>
                    <span className="bg-green-100 text-green-800 text-xs font-bold px-2.5 py-0.5 rounded-full">
                      Live Feed
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5">
                    <FiCalendar size={12} className="text-gray-400" />
                    <span>{todayFormatted}</span>
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => fetchTodayUsers(true)}
                  disabled={refreshing || loading}
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 px-3.5 py-2 rounded-xl transition shadow-sm cursor-pointer"
                >
                  <FiRefreshCw
                    size={13}
                    className={refreshing ? "animate-spin text-green-600" : ""}
                  />
                  <span>Refresh Live Feed</span>
                </button>

                <button
                  onClick={() => navigate("/user")}
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 px-3.5 py-2 rounded-xl transition shadow-sm cursor-pointer"
                >
                  <span>All Users</span>
                  <FiArrowRight size={13} />
                </button>
              </div>
            </div>
          </div>

          {/* 4 Key Metrics for Today */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Today Registrations */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center gap-2 text-xs font-semibold text-green-600 uppercase tracking-wider mb-1">
                <FiUserPlus size={15} /> Registrations Today
              </div>
              <p className="text-2xl font-extrabold text-gray-800">
                {totalTodayCount}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                New accounts created today
              </p>
            </div>

            {/* Active Mining Today */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center gap-2 text-xs font-semibold text-yellow-600 uppercase tracking-wider mb-1">
                <FiZap size={15} /> Started Mining
              </div>
              <p className="text-2xl font-extrabold text-gray-800">
                {activeMinersToday}
                <span className="text-xs font-normal text-gray-400 ml-1">
                  / {totalTodayCount} today
                </span>
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Currently mining active
              </p>
            </div>

            {/* Joined via Referral */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center gap-2 text-xs font-semibold text-purple-600 uppercase tracking-wider mb-1">
                <FiShare2 size={15} /> Referred Users
              </div>
              <p className="text-2xl font-extrabold text-gray-800">
                {referredUsersToday}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Invited by existing members
              </p>
            </div>

            {/* Total Coins Accumulated */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">
                <FiDollarSign size={15} /> Coins Mined Today
              </div>
              <p className="text-2xl font-extrabold text-gray-800">
                {totalCoinsToday.toFixed(2)}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Total balance of new users
              </p>
            </div>
          </div>

          {/* Search, Filter & Controls Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              {/* Search Bar */}
              <div className="relative flex-1">
                <FiSearch
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                  size={15}
                />
                <input
                  type="text"
                  placeholder="Search by username, email, ID, referral code, or device UUID..."
                  value={search}
                  onChange={handleSearchChange}
                  className="w-full pl-9 pr-9 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white"
                />
                {search && (
                  <button
                    onClick={() => {
                      setSearch("");
                      setDebouncedSearch("");
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX size={14} />
                  </button>
                )}
              </div>

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-2.5">
                {/* Provider Filter */}
                <select
                  value={providerFilter}
                  onChange={(e) => {
                    setProviderFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-gray-50 border border-gray-200 text-xs font-semibold text-gray-700 px-3 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                >
                  <option value="All">All Providers</option>
                  <option value="google">Google</option>
                  <option value="apple">Apple</option>
                  <option value="email">Email</option>
                </select>

                {/* Mining Filter */}
                <select
                  value={miningFilter}
                  onChange={(e) => {
                    setMiningFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-gray-50 border border-gray-200 text-xs font-semibold text-gray-700 px-3 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                >
                  <option value="All">All Mining Status</option>
                  <option value="active">Active Miners Only</option>
                  <option value="inactive">Inactive Only</option>
                </select>

                {/* Sort Filter */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-gray-50 border border-gray-200 text-xs font-semibold text-gray-700 px-3 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                >
                  <option value="newest">Newest First</option>
                  <option value="highest_coins">Highest Coins</option>
                  <option value="oldest">Oldest Today</option>
                </select>
              </div>
            </div>
          </div>

          {/* User Details Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <FiUsers size={16} className="text-gray-500" />
                <span>Today's User List ({filteredUsers.length} total today)</span>
              </h3>
              <p className="text-xs text-gray-400">
                Page {currentPage} of {totalPages}
              </p>
            </div>

            {loading ? (
              <div className="flex flex-col justify-center items-center py-24 gap-3 text-gray-400 text-sm">
                <Spinner />
                <span>Fetching today's registered accounts...</span>
              </div>
            ) : error ? (
              <div className="p-8 text-center text-red-600 text-sm bg-red-50">
                {error}
              </div>
            ) : displayedUsers.length === 0 ? (
              <div className="py-24 flex flex-col items-center justify-center text-center text-gray-400">
                <FiUserPlus size={42} className="text-gray-300 mb-3" />
                <p className="text-base font-semibold text-gray-700">
                  No Users Registered Today
                </p>
                <p className="text-xs text-gray-400 mt-1 max-w-sm">
                  {search
                    ? "No registered accounts match your search filter."
                    : "No new user accounts have registered yet today."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wider">
                      <th className="px-5 py-3.5 w-12">#</th>
                      <th className="px-5 py-3.5">User</th>
                      <th className="px-5 py-3.5">Email</th>
                      <th className="px-5 py-3.5">Registered Time</th>
                      <th className="px-5 py-3.5">Provider</th>
                      <th className="px-5 py-3.5 hidden md:table-cell">Device UUID</th>
                      <th className="px-5 py-3.5">Referral Info</th>
                      <th className="px-5 py-3.5 text-center">Mining</th>
                      <th className="px-5 py-3.5 text-right">Coins</th>
                      <th className="px-5 py-3.5 text-center min-w-[240px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {displayedUsers.map((user, index) => {
                      const initials = (user.username || "U")[0].toUpperCase();
                      const avatarColor = getAvatarColor(user.username || "");
                      const isBanned = user.isBanned || user.status === "Banned";
                      const rowNum = (currentPage - 1) * itemsPerPage + index + 1;

                      return (
                        <tr
                          key={user.userId || user.id || index}
                          className="hover:bg-gray-50 transition"
                        >
                          <td className="px-5 py-4 text-xs text-gray-400">
                            {rowNum}
                          </td>

                          {/* User Avatar & Username */}
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarColor}`}
                              >
                                {initials}
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-bold text-gray-800 truncate">
                                    {user.username || "—"}
                                  </span>
                                  {isBanned && (
                                    <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.2 rounded font-bold">
                                      Banned
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-400 font-mono">
                                  #{user.userId || user.id}
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* Email */}
                          <td className="px-5 py-4 text-gray-600">
                            <div className="flex items-center gap-1.5">
                              <span className="truncate max-w-[200px]">
                                {user.email || "—"}
                              </span>
                              {user.email && (
                                <button
                                  onClick={() =>
                                    copyText(user.email, `em-${user.userId || user.id}`)
                                  }
                                  className="text-gray-300 hover:text-gray-600"
                                  title="Copy email"
                                >
                                  {copiedId === `em-${user.userId || user.id}` ? (
                                    <FiCheck size={11} className="text-green-600" />
                                  ) : (
                                    <FiCopy size={11} />
                                  )}
                                </button>
                              )}
                            </div>
                          </td>

                          {/* Registered Time */}
                          <td className="px-5 py-4 text-gray-700 font-medium whitespace-nowrap">
                            <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs font-mono font-semibold">
                              {formatTimeOnly(user.createdAt)}
                            </span>
                          </td>

                          {/* Provider */}
                          <td className="px-5 py-4">
                            <span
                              className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                                (user.providerType || "").toLowerCase() === "google"
                                  ? "bg-rose-50 text-rose-600"
                                  : (user.providerType || "").toLowerCase() === "apple"
                                  ? "bg-gray-100 text-gray-700"
                                  : "bg-blue-50 text-blue-600"
                              }`}
                            >
                              {user.providerType || "Email"}
                            </span>
                          </td>

                          {/* Device UUID */}
                          <td className="px-5 py-4 hidden md:table-cell">
                            <div className="flex items-center gap-1.5">
                              <span className="font-mono text-xs text-gray-500 truncate max-w-[130px]">
                                {user.uuid || "—"}
                              </span>
                              {user.uuid && (
                                <button
                                  onClick={() =>
                                    copyText(user.uuid, `uuid-${user.userId || user.id}`)
                                  }
                                  className="text-gray-300 hover:text-gray-600"
                                  title="Copy Device UUID"
                                >
                                  {copiedId === `uuid-${user.userId || user.id}` ? (
                                    <FiCheck size={11} className="text-green-600" />
                                  ) : (
                                    <FiCopy size={11} />
                                  )}
                                </button>
                              )}
                            </div>
                          </td>

                          {/* Referral Info */}
                          <td className="px-5 py-4 text-xs">
                            {user.referralBy ? (
                              <div>
                                <span className="text-gray-400">By: </span>
                                <span className="font-mono font-semibold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                                  {user.referralBy}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400">Direct</span>
                            )}
                          </td>

                          {/* Mining Status */}
                          <td className="px-5 py-4 text-center">
                            <span
                              className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                user.isMiningActive
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {user.isMiningActive ? "Active" : "Inactive"}
                            </span>
                          </td>

                          {/* Coins */}
                          <td className="px-5 py-4 text-right font-bold text-gray-800">
                            {parseFloat(user.coin || 0).toFixed(2)}
                          </td>

                          {/* Actions */}
                          <td className="px-5 py-4 text-center">
                            <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                              {/* Coin History */}
                              <button
                                onClick={() =>
                                  navigate(`/user/${user.userId || user.id}/coin-history`)
                                }
                                className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-300 transition shadow-2xs"
                                title="View Unified Coin History Ledger"
                              >
                                <span>🪙</span> Ledger
                              </button>

                              {/* View Profile */}
                              <button
                                onClick={() =>
                                  navigate(`/user/${user.userId || user.id}`)
                                }
                                className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-[#ffff00] text-black border border-gray-200 transition shadow-sm"
                                title="Inspect Full User Details"
                              >
                                <FiExternalLink size={12} /> Profile
                              </button>

                              {/* Direct Email */}
                              <button
                                onClick={() => handleOpenMailModal(user)}
                                className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 transition shadow-sm"
                                title="Send email to user"
                              >
                                <FiMail size={12} /> Mail
                              </button>

                              {/* Ban */}
                              <button
                                onClick={() => handleOpenBanModal(user)}
                                disabled={isBanned}
                                className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition shadow-sm ${
                                  isBanned
                                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                    : "bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200"
                                }`}
                                title={
                                  isBanned
                                    ? "User is already banned"
                                    : "Ban user and stop mining"
                                }
                              >
                                <FiSlash size={12} /> {isBanned ? "Banned" : "Ban"}
                              </button>

                              {/* Delete */}
                              <button
                                onClick={() => handleOpenDeleteModal(user)}
                                className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 transition shadow-sm"
                                title="Delete user permanently"
                              >
                                <FiTrash2 size={12} /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                <span>
                  Showing {displayedUsers.length} of {filteredUsers.length} today users
                </span>
                <div className="flex items-center gap-1">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-100 disabled:opacity-40"
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`px-3 py-1.5 rounded-lg border font-semibold ${
                        currentPage === p
                          ? "bg-[#ffff00] text-black border-[#ffff00]"
                          : "bg-white text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    className="px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-100 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ================= DIRECT EMAIL MODAL ================= */}
      {mailModalOpen && selectedUserForMail && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => !mailLoading && setMailModalOpen(false)}
        >
          <div
            className="bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 bg-blue-50 border-b border-blue-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
                  <FiMail size={16} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-sm">
                    Send Email to {selectedUserForMail.username || "User"}
                  </h3>
                  <p className="text-[11px] text-blue-700">
                    To: {selectedUserForMail.email}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setMailModalOpen(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={mailSubject}
                  onChange={(e) => setMailSubject(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Message Content
                </label>
                <textarea
                  rows={6}
                  value={mailContent}
                  onChange={(e) => setMailContent(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none leading-relaxed"
                />
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                type="button"
                disabled={mailLoading}
                onClick={() => setMailModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded-xl transition"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={mailLoading}
                onClick={handleSendEmail}
                className="flex items-center gap-2 px-5 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow transition disabled:opacity-60"
              >
                {mailLoading ? (
                  <>
                    <FiRefreshCw size={14} className="animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    <FiSend size={14} /> Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= BAN MODAL ================= */}
      {banModalOpen && selectedUserForBan && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => !banLoading && setBanModalOpen(false)}
        >
          <div
            className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 bg-amber-50 border-b border-amber-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                  <FiAlertTriangle size={16} />
                </div>
                <h3 className="font-bold text-gray-800 text-base">
                  Ban User #{selectedUserForBan.userId || selectedUserForBan.id}
                </h3>
              </div>
              <button
                onClick={() => setBanModalOpen(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Ban Reason
                </label>
                <input
                  type="text"
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>

              <div
                onClick={() => setRevertCoins(!revertCoins)}
                className="border border-red-200 bg-red-50 rounded-xl p-3 flex items-start gap-2.5 cursor-pointer select-none"
              >
                {revertCoins ? (
                  <FiCheckSquare size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <FiSquare size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <p className="text-xs font-bold text-red-900">
                    Revert Coins (Reset to 0.00)
                  </p>
                  <p className="text-[11px] text-red-700">
                    Claw back coins earned by this user.
                  </p>
                </div>
              </div>

              <div
                onClick={() => setSendWarningEmail(!sendWarningEmail)}
                className="border border-blue-100 bg-blue-50/60 rounded-xl p-3 flex items-start gap-2.5 cursor-pointer select-none"
              >
                {sendWarningEmail ? (
                  <FiCheckSquare size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <FiSquare size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <p className="text-xs font-bold text-gray-800">
                    Send Suspension Email
                  </p>
                  <p className="text-[11px] text-gray-500">
                    Notify {selectedUserForBan.email} of account ban.
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                type="button"
                disabled={banLoading}
                onClick={() => setBanModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded-xl transition"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={banLoading}
                onClick={handleConfirmBan}
                className="flex items-center gap-2 px-5 py-2 text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-xl shadow transition disabled:opacity-60"
              >
                {banLoading ? (
                  <>
                    <FiRefreshCw size={14} className="animate-spin" /> Banning...
                  </>
                ) : (
                  <>
                    <FiSlash size={14} /> Confirm Ban
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {deleteModalOpen && selectedUserForDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => !deleteLoading && setDeleteModalOpen(false)}
        >
          <div
            className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4 border border-red-100">
                <FiTrash2 size={26} />
              </div>

              <h3 className="text-lg font-bold text-gray-800 mb-1">
                Delete User Account?
              </h3>
              <p className="text-xs text-gray-500 mb-4">
                Are you sure you want to permanently delete today's user{" "}
                <strong className="text-gray-800 font-semibold">
                  {selectedUserForDelete.username || selectedUserForDelete.email || "Account"}
                </strong>{" "}
                (#{selectedUserForDelete.userId || selectedUserForDelete.id})?
              </p>

              {/* User Details Summary */}
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-3.5 text-xs text-left mb-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">User ID:</span>
                  <span className="font-bold text-gray-800">
                    #{selectedUserForDelete.userId || selectedUserForDelete.id}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Username:</span>
                  <span className="font-semibold text-gray-700">
                    {selectedUserForDelete.username || "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Email:</span>
                  <span className="font-mono text-gray-700 truncate max-w-[200px]">
                    {selectedUserForDelete.email || "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Total Coins:</span>
                  <span className="font-bold text-amber-600">
                    {parseFloat(selectedUserForDelete.coin || 0).toFixed(2)} ZX
                  </span>
                </div>
              </div>

              <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-xs text-red-700 text-left mb-5 flex items-start gap-2">
                <FiAlertCircle size={16} className="flex-shrink-0 mt-0.5 text-red-500" />
                <span>
                  This action is permanent and irreversible. The user's account, mining sessions, coins, and referral records will be deleted.
                </span>
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  disabled={deleteLoading}
                  onClick={() => setDeleteModalOpen(false)}
                  className="w-full py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={deleteLoading}
                  onClick={handleConfirmDelete}
                  className="w-full py-2.5 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow transition disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {deleteLoading ? (
                    <>
                      <FiRefreshCw size={14} className="animate-spin" /> Deleting...
                    </>
                  ) : (
                    <>
                      <FiTrash2 size={14} /> Yes, Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TodayUsers;
